import React, { Fragment, useMemo, useState } from 'react';
import {
  AlertTriangle, BrainCircuit, Check, CheckCircle2, ChevronDown, Clipboard,
  Database, FileSearch, FileText, LoaderCircle, Search, ShieldCheck, Sparkles,
  Zap,
} from 'lucide-react';
import type {
  GeoAgentActivity, GeoAgentDocumentSource, GeoAgentGrounding, GeoAgentMode, PreviewAnswer,
} from '../../lib/api/geologist';
import GeologistAnswer from './GeologistAnswer';
import type { GeoUiLang } from './geoWorkspaceI18n';

type TurnState = 'pending' | 'done' | 'error' | 'stopped';

const COPY = {
  ru: {
    role: 'Профессиональный AI-геолог', standard: 'Экспертный', deep: 'Глубокий анализ',
    model: 'GPT-5.6 Sol', copy: 'Копировать', copied: 'Скопировано', working: 'Формирую ответ…',
    verified: 'Проверенные данные, расчёты и визуализации', documents: 'Документы, использованные в поиске',
    evidence: 'Проверенный слой данных', noSearch: 'Поиск по полным документам был недоступен в этом ответе.',
    cached: 'Умный кэш', cacheHint: 'Проверенный ответ использован повторно без нового запроса к AI.',
    tokensSaved: 'AI-токенов сэкономлено', minutes: 'мин',
    stopped: 'Генерация остановлена пользователем.',
    archiveCensus: 'Весь архив · статусы раскрыты',
    catalogue1975: 'Каталог 1975 · полная структурированная выборка',
    catalogueRows: 'строк каталога 1975',
    catalogueMatches: 'точных совпадений',
  },
  hy: {
    role: 'Մասնագիտական AI-երկրաբան', standard: 'Փորձագիտական', deep: 'Խոր վերլուծություն',
    model: 'GPT-5.6 Sol', copy: 'Պատճենել', copied: 'Պատճենված է', working: 'Կազմում եմ պատասխանը…',
    verified: 'Ստուգված տվյալներ, հաշվարկներ և պատկերումներ', documents: 'Որոնման մեջ օգտագործված փաստաթղթեր',
    evidence: 'Ստուգված տվյալների շերտ', noSearch: 'Ամբողջական փաստաթղթերի որոնումն այս պատասխանում հասանելի չէր։',
    cached: 'Խելացի քեշ', cacheHint: 'Ստուգված պատասխանը վերօգտագործվել է առանց AI-ի նոր հարցման։',
    tokensSaved: 'խնայված AI թոքեն', minutes: 'րոպե',
    stopped: 'Պատասխանի ստեղծումը դադարեցվել է օգտատիրոջ կողմից։',
    archiveCensus: 'Ամբողջ արխիվ · կարգավիճակները բացահայտված են',
    catalogue1975: '1975 կատալոգ · ամբողջական կառուցվածքային շերտ',
    catalogueRows: '1975 կատալոգի տող',
    catalogueMatches: 'ճշգրիտ համընկնում',
  },
  en: {
    role: 'Professional AI geologist', standard: 'Expert', deep: 'Deep analysis',
    model: 'GPT-5.6 Sol', copy: 'Copy', copied: 'Copied', working: 'Composing the answer…',
    verified: 'Verified data, calculations, and visualisations', documents: 'Documents used in search',
    evidence: 'Verified data layer', noSearch: 'Full-document search was unavailable for this answer.',
    cached: 'Smart cache', cacheHint: 'A verified answer was reused without a new AI request.',
    tokensSaved: 'AI tokens saved', minutes: 'min',
    stopped: 'Generation was stopped by the user.',
    archiveCensus: 'Whole archive · statuses disclosed',
    catalogue1975: '1975 catalogue · complete structured layer',
    catalogueRows: '1975 catalogue rows',
    catalogueMatches: 'exact matches',
  },
} satisfies Record<GeoUiLang, Record<string, string>>;

function InlineText({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\])/g).filter(Boolean);
  return <>{parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={index}>{part.slice(1, -1)}</code>;
    }
    if (part.startsWith('[') && part.endsWith(']')) {
      return <span className="geo-agent-citation" key={index}>{part}</span>;
    }
    return <Fragment key={index}>{part}</Fragment>;
  })}</>;
}

function MarkdownTable({ lines }: { lines: string[] }) {
  const cells = (line: string) => line.replace(/^\||\|$/g, '').split('|').map((cell) => cell.trim());
  const header = cells(lines[0]);
  const rows = lines.slice(2).map(cells);
  return (
    <div className="geo-agent-markdown-table">
      <table>
        <thead><tr>{header.map((cell, i) => <th key={i}><InlineText text={cell} /></th>)}</tr></thead>
        <tbody>{rows.map((row, r) => <tr key={r}>{row.map((cell, c) => <td key={c}><InlineText text={cell} /></td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function AgentMarkdown({ text }: { text: string }) {
  const blocks = useMemo(() => {
    const lines = text.replace(/\r/g, '').split('\n');
    const nodes: React.ReactNode[] = [];
    let index = 0;
    while (index < lines.length) {
      const line = lines[index].trimEnd();
      if (!line.trim()) { index += 1; continue; }
      if (line.includes('|') && index + 1 < lines.length && /^\s*\|?\s*:?-{3,}/.test(lines[index + 1])) {
        const tableLines = [line, lines[index + 1]];
        index += 2;
        while (index < lines.length && lines[index].includes('|') && lines[index].trim()) {
          tableLines.push(lines[index]); index += 1;
        }
        nodes.push(<MarkdownTable key={`table-${index}`} lines={tableLines} />);
        continue;
      }
      const heading = line.match(/^(#{1,3})\s+(.+)$/);
      if (heading) {
        const level = heading[1].length;
        const content = <InlineText text={heading[2]} />;
        nodes.push(level === 1 ? <h2 key={index}>{content}</h2>
          : level === 2 ? <h3 key={index}>{content}</h3>
            : <h4 key={index}>{content}</h4>);
        index += 1; continue;
      }
      const bullet = line.match(/^[-*]\s+(.+)$/);
      const numbered = line.match(/^\d+[.)]\s+(.+)$/);
      if (bullet || numbered) {
        const ordered = Boolean(numbered);
        const items: string[] = [];
        while (index < lines.length) {
          const match = ordered
            ? lines[index].trim().match(/^\d+[.)]\s+(.+)$/)
            : lines[index].trim().match(/^[-*]\s+(.+)$/);
          if (!match) break;
          items.push(match[1]); index += 1;
        }
        const children = items.map((item, i) => <li key={i}><InlineText text={item} /></li>);
        nodes.push(ordered ? <ol key={`list-${index}`}>{children}</ol> : <ul key={`list-${index}`}>{children}</ul>);
        continue;
      }
      if (line.startsWith('> ')) {
        nodes.push(<blockquote key={index}><InlineText text={line.slice(2)} /></blockquote>);
        index += 1; continue;
      }
      const paragraph = [line.trim()];
      index += 1;
      while (index < lines.length && lines[index].trim()
        && !/^(#{1,3})\s+/.test(lines[index])
        && !/^[-*]\s+/.test(lines[index].trim())
        && !/^\d+[.)]\s+/.test(lines[index].trim())) {
        paragraph.push(lines[index].trim()); index += 1;
      }
      nodes.push(<p key={`p-${index}`}><InlineText text={paragraph.join(' ')} /></p>);
    }
    return nodes;
  }, [text]);
  return <div className="geo-agent-prose">{blocks}</div>;
}

function ActivityIcon({ activity }: { activity: GeoAgentActivity }) {
  if (activity.status === 'done') return <CheckCircle2 size={14} />;
  if (activity.status === 'warning') return <AlertTriangle size={14} />;
  if (activity.id === 'documents') return <Search size={14} />;
  if (activity.id === 'reasoning') return <BrainCircuit size={14} />;
  if (activity.id === 'writing') return <Sparkles size={14} />;
  if (activity.id === 'cache') return <Zap size={14} />;
  return <LoaderCircle size={14} />;
}

function sourceLabel(filename: string): string {
  return filename.replace(/^reviewed-/, '').replace(/^project-/, '').replace(/\.md$/i, '').replace(/[-_]+/g, ' ');
}

export default function GeoAgentResponse({
  text, status, activities, verified, sources, lang, mode, model, documentSearch,
  cacheHit, cacheAgeSeconds, tokensAvoided, grounding, onOpenSource,
}: {
  text: string;
  status: TurnState;
  activities: GeoAgentActivity[];
  verified?: PreviewAnswer;
  grounding?: GeoAgentGrounding;
  sources: GeoAgentDocumentSource[];
  lang: GeoUiLang;
  mode: GeoAgentMode;
  model?: string;
  documentSearch?: boolean;
  cacheHit?: boolean;
  cacheAgeSeconds?: number;
  tokensAvoided?: number;
  onOpenSource: (sourceId?: string | null, label?: string) => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = COPY[lang];
  const copyAnswer = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  return (
    <article className="geo-agent-response" data-testid="geo-agent-response">
      <header className="geo-agent-response__header">
        <div>
          <span className="geo-agent-response__role"><Sparkles size={14} />{copy.role}</span>
          <div className="geo-agent-badges">
            <span><BrainCircuit size={13} />{model || copy.model}</span>
            <span className={mode === 'deep' ? 'is-deep' : ''}>{mode === 'deep' ? copy.deep : copy.standard}</span>
            {documentSearch && <span className="is-evidence"><FileSearch size={13} />RAG</span>}
            {grounding?.archive_wide_borehole_census && <span className="is-evidence"><Database size={13} />{copy.archiveCensus}</span>}
            {grounding?.coordinate_catalogue_1975?.included && <span className="is-evidence" data-testid="catalogue-1975-badge"><Database size={13} />{copy.catalogue1975}</span>}
            {cacheHit && (
              <span
                className="is-cache"
                data-testid="smart-cache-badge"
                title={`${copy.cacheHint}${tokensAvoided ? ` ${tokensAvoided} ${copy.tokensSaved}.` : ''}${
                  cacheAgeSeconds !== undefined ? ` (${Math.max(0, Math.round(cacheAgeSeconds / 60))} ${copy.minutes})` : ''
                }`}
              >
                <Zap size={13} />{copy.cached}{tokensAvoided ? ` · ${tokensAvoided}` : ''}
              </span>
            )}
          </div>
        </div>
        {text && (
          <button className="geo-agent-copy" onClick={copyAnswer} aria-label={copy.copy}>
            {copied ? <Check size={14} /> : <Clipboard size={14} />}{copied ? copy.copied : copy.copy}
          </button>
        )}
      </header>

      {activities.length > 0 && (
        <div className="geo-agent-activity" aria-label="Agent activity">
          {activities.map((activity) => (
            <span key={activity.id} className={`is-${activity.status}`}>
              <ActivityIcon activity={activity} />{activity.label}
            </span>
          ))}
        </div>
      )}

      {text ? <AgentMarkdown text={text} /> : status === 'pending' ? (
        <div className="geo-agent-waiting"><LoaderCircle size={17} />{copy.working}</div>
      ) : null}
      {status === 'pending' && text && <span className="geo-agent-stream-caret" aria-hidden="true" />}
      {status === 'stopped' && <div className="geo-agent-stopped">{copy.stopped}</div>}
      {status === 'done' && documentSearch === false && (
        <div className="geo-agent-index-warning"><AlertTriangle size={14} />{copy.noSearch}</div>
      )}

      {sources.length > 0 && (
        <section className="geo-agent-documents">
          <div><FileText size={15} /><strong>{copy.documents}</strong></div>
          <div className="geo-agent-document-chips">
            {sources.map((source) => {
              const label = sourceLabel(source.filename);
              const reviewed = source.filename.startsWith('reviewed-');
              return source.id || reviewed ? (
                <button key={`${source.id || ''}-${source.filename}`} onClick={() => onOpenSource(source.id, source.filename)}>
                  {source.source_group ? `${source.source_group} · ` : ''}{label}{source.page ? ` · p. ${source.page}` : ''}
                </button>
              ) : <span key={source.filename}>{label}</span>;
            })}
          </div>
        </section>
      )}

      {grounding && (
        <details className="gv2-grounding-trace">
          <summary><Database size={15} /><span><strong>{copy.evidence}</strong><small>{grounding.archive_coverage ? `${grounding.archive_coverage.summary.included_source_groups}/${grounding.archive_coverage.summary.controlled_source_groups} groups · ` : ''}{grounding.assertion_count} assertions · {grounding.chunk_count} chunks · {grounding.sources.length} sources</small></span><ChevronDown size={15} /></summary>
          <div>
            <span className="gv2-scope-pill"><ShieldCheck size={12} />{grounding.knowledge_scope}{grounding.scope_auto_expanded ? ' · auto-expanded' : ''}</span>
            <div className="gv2-grounding-metrics"><span><b>{grounding.conflict_count}</b> conflicts</span><span><b>{grounding.gap_count}</b> gaps</span>{grounding.archive_coverage && <span><b>{grounding.archive_coverage.summary.embedded_chunks}</b> semantic chunks</span>}{grounding.coordinate_catalogue_1975?.included && grounding.coordinate_catalogue_1975.summary && <><span data-testid="catalogue-1975-row-count"><b>{grounding.coordinate_catalogue_1975.summary.catalogue_records}</b> {copy.catalogueRows}</span><span><b>{grounding.coordinate_catalogue_1975.matching_record_count}</b> {copy.catalogueMatches}</span></>}<span><b>{grounding.corpus_fingerprint.slice(0, 10)}</b> corpus</span></div>
            {grounding.evidence_gaps.slice(0, 4).map((gap) => <p key={gap.id}><AlertTriangle size={13} /><span><strong>{gap.group_key}</strong> {gap.label}</span></p>)}
          </div>
        </details>
      )}

      {verified && (
        <details className="geo-agent-evidence-layer">
          <summary>
            <span><Database size={16} /></span>
            <div><strong>{copy.verified}</strong><small><ShieldCheck size={12} />{copy.evidence}</small></div>
            <ChevronDown size={16} />
          </summary>
          <div className="geo-agent-evidence-layer__body">
            <GeologistAnswer answer={verified} lang={lang} onOpenSource={onOpenSource} />
          </div>
        </details>
      )}
    </article>
  );
}
